// eslint-disable-next-line import/no-extraneous-dependencies
import React, { ComponentType, useEffect, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { WixProvider } from '@wix/sdk-react';
import {
  dashboard,
  Host,
  LocalHost,
  ObserveState,
  RemoteHost,
} from '@wix/dashboard';

export const withDashboard = <P extends {} = {}>(
  Component: ComponentType<P>,
): ComponentType<P & { host?: LocalHost }> => {
  return (ownPropsWithHost) => {
    const { host: maybeGivenHost, ...ownProps } = ownPropsWithHost;
    const { host, props: hostProps } = useHostAndProps<P>(
      ownPropsWithHost.host,
    );
    const [auth] = useState(dashboard.auth);

    if (!host || !hostProps) {
      return null;
    }
    return (
      <WixProvider host={host} auth={auth}>
        <Component {...hostProps} {...ownProps} />
      </WixProvider>
    );
  };
};

const useHostAndProps = <P extends {}>(
  localHost?: LocalHost,
): { host?: Host; props?: P } => {
  const [remoteHost, setRemoteHost] = useState<RemoteHost | undefined>();
  const [remoteHostProps, setRemoteHostProps] = useState<P | undefined>();

  useEffect(() => {
    if (localHost) {
      return;
    }

    const _remoteHost = dashboard.host();
    setRemoteHost(_remoteHost);

    const observeState = _remoteHost.channel.observeState as (
      ...args: Parameters<ObserveState>
    ) => ReturnType<Host['channel']['observeState']>; // casting is needed because typescript couldn't handle it by itself
    const observeResult = observeState((newObservedProps) => {
      setRemoteHostProps(newObservedProps as P);
    });

    const cleanup = async () => {
      setRemoteHost(undefined);
      setRemoteHostProps(undefined);
      (await observeResult).disconnect();
      _remoteHost.close();
    };

    return () => {
      cleanup();
    };
  }, [localHost]);

  return localHost
    ? { host: localHost, props: {} as P }
    : { host: remoteHost, props: remoteHostProps };
};
